import { useEffect, type RefObject } from 'react';

const FOCUSABLE =
  'a[href],button:not([disabled]),textarea:not([disabled]),input:not([disabled]):not([type="hidden"]),select:not([disabled]),[tabindex]:not([tabindex="-1"])';

let openLayers = 0;

/** Traps Tab focus inside `ref`, closes on Escape, restores focus and body scroll on close. */
export function useFocusTrap(ref: RefObject<HTMLElement | null>, open: boolean, onEscape?: () => void) {
  useEffect(() => {
    if (!open) return;
    const previous = document.activeElement as HTMLElement | null;
    const node = ref.current;
    openLayers++;
    document.body.style.overflow = 'hidden';

    const focusFirst = () => {
      if (!node) return;
      const preferred = node.querySelector<HTMLElement>('[data-autofocus]');
      const first = preferred ?? node.querySelector<HTMLElement>(FOCUSABLE);
      (first ?? node).focus();
    };
    const t = window.setTimeout(focusFirst, 0);

    const onKey = (e: KeyboardEvent) => {
      if (!node) return;
      if (e.key === 'Escape') {
        // only the top-most layer handles Escape
        if (node.contains(document.activeElement) || document.activeElement === document.body) {
          e.stopPropagation();
          onEscape?.();
        }
        return;
      }
      if (e.key !== 'Tab') return;
      const items = Array.from(node.querySelectorAll<HTMLElement>(FOCUSABLE)).filter((el) => el.offsetParent !== null || el === document.activeElement);
      if (items.length === 0) {
        e.preventDefault();
        return;
      }
      const first = items[0]!;
      const last = items[items.length - 1]!;
      if (e.shiftKey && document.activeElement === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    };
    node?.addEventListener('keydown', onKey);
    return () => {
      window.clearTimeout(t);
      node?.removeEventListener('keydown', onKey);
      openLayers--;
      if (openLayers <= 0) {
        openLayers = 0;
        document.body.style.overflow = '';
      }
      previous?.focus?.();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);
}
